import type {
  BoxGeometry,
  BufferGeometry,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  PolyhedronGeometry,
  ShapeGeometry,
} from 'three';
import { Vector3 } from 'three';

import { OBJECT_EDIT_YELLOW } from '@/config/color';
import { CAMERA_FAR_2D, OBJECT_FACE_OPACITY } from '@/config/render';
import { FatLine } from '@/lib/three';
import type { ILineOptions, IMeshOptions, IObject3DStatus, WidgetObject, WidgetType } from '@/types';

import { BaseCamera } from '../camera';
import { EnhancedObject3D } from '../graphics';
import { AdditionHandlerType, AnnoHandlerType } from '../tool';
import { AnnoCompoundItem } from './AnnoCompoundItem';
import { AnnoInstanceItem } from './AnnoInstanceItem';
import { Box2d } from './Box2d';
import { Cuboid } from './Cuboid';
import { Line2d } from './Line2d';
import { Line3d } from './Line3d';
import { Point3d } from './Point3d';
import { Poly3d } from './Poly3d';
import { Pyramid3d } from './Pyramid3d';
import { Spline3d } from './Spline3d';

const BORDER_THICKNESS_NORMAL = 1;
const BORDER_THICKNESS_BOLD = 2;
const POINT_SIZE_INCREMENT = 2;
const BORDER_OBJECTS = ['point3d', 'cuboid', 'pyramid']; // 边框需要加粗的类型

export class AnnoFactory {
  // TODO: (xiaonan) add create & dispose function
  static createFromWidget(name: string, widget: WidgetObject, matrix: Matrix4, options: IMeshOptions | ILineOptions) {
    let result: Object3D | null = null;

    switch (AnnoFactory.getWidgetType(widget)) {
      case 'box2d':
        result = Box2d.createFromWidget(name, widget, matrix, {
          opacity: OBJECT_FACE_OPACITY,
          ...options,
        });
        break;
      case 'cuboid':
        result = Cuboid.createFromWidget(name, widget, matrix, {
          opacity: OBJECT_FACE_OPACITY,
          ...options,
        });
        break;
      case 'line3d':
        result = Line3d.createFromWidget(name, widget, matrix, {
          growable: true,
          withDot: true,
          ...options,
        });
        break;
      case 'spline3d':
        result = Spline3d.createFromWidget(name, widget, matrix, {
          growable: true,
          withDot: true,
          ...options,
        });
        break;
      case 'line2d':
        result = Line2d.createFromWidget(name, widget, matrix, {
          growable: true,
          withDot: true,
          ...options,
        });
        break;
      case 'point3d':
        result = Point3d.createFromWidget(name, widget, matrix, {
          ...options,
        });
        break;
      case 'poly3d':
        result = Poly3d.createFromWidget(name, widget, matrix, {
          growable: true,
          withDot: true,
          ...options,
        });
        break;
    }

    // 将 widget 挂载到 Object3D 对象数据中，便于比较 IAnnoItem 数据与渲染几何数据是否一致
    result && (result.userData.widget = widget);
    return result;
  }

  static updateFromWidget(target: Object3D, widget: WidgetObject, matrix: Matrix4) {
    target.userData.widget = widget;
    switch (AnnoFactory.getWidgetType(widget)) {
      case 'box2d':
        return Box2d.updateFromWidget(target as Mesh<ShapeGeometry, MeshBasicMaterial>, widget, matrix);
      case 'cuboid':
        return Cuboid.updateFromWidget(target as Mesh<BufferGeometry, MeshBasicMaterial>, widget, matrix);
      case 'line3d':
        return Line3d.updateFromWidget(target as FatLine, widget, matrix);
      case 'spline3d':
        return Spline3d.updateFromWidget(target as FatLine, widget, matrix);
      case 'line2d':
        return Line2d.updateFromWidget(target as FatLine, widget, matrix);
      case 'point3d':
        return Point3d.updateFromWidget(target as Mesh<BoxGeometry, MeshBasicMaterial>, widget, matrix);
      case 'poly3d':
        return Poly3d.updateFromWidget(target as FatLine, widget, matrix);
      default:
        break;
    }
  }

  static transOutWidget(
    target: Object3D,
    matrix: Matrix4,
    camera?: PerspectiveCamera | OrthographicCamera,
    pointCloud?: Object3D
  ) {
    let result: WidgetObject | null = null;

    switch (target.userData.type) {
      case 'box2d':
        result = Box2d.transOutWidget(target as Mesh<BufferGeometry, MeshBasicMaterial>, matrix);
        break;
      case 'cuboid':
        result = Cuboid.transOutWidget(target as Mesh<BufferGeometry, MeshBasicMaterial>, matrix, camera, pointCloud);
        break;
      case 'line3d':
        result = Line3d.transOutWidget(target as FatLine, matrix);
        break;
      case 'spline3d':
        result = Spline3d.transOutWidget(target as FatLine, matrix);
        break;
      case 'line2d':
        result = Line2d.transOutWidget(target as FatLine, matrix);
        break;
      case 'point3d':
        result = Point3d.transOutWidget(target as Mesh<BoxGeometry, MeshBasicMaterial>, matrix);
        break;
      case 'poly3d':
        result = Poly3d.transOutWidget(target as FatLine, matrix);
        break;
    }

    result && (target.userData.widget = result);
    return result;
  }

  /**
   * 从 JSON Object 对象生成 IAnnoItem
   * @param obj
   * @returns
   */
  static createFromObject(obj: Record<string, any>) {
    if (AnnoInstanceItem.isAnnoInstanceObject(obj)) {
      return AnnoInstanceItem.createFromObject(obj);
    }

    if (AnnoCompoundItem.isAnnoCompoundObject(obj)) {
      return AnnoCompoundItem.createFromObject(obj);
    }

    return null;
  }

  /**
   * 修改 widget 数据到目标的 position
   * @param widget
   * @param position
   * @param matrix 从 threejs 到文件坐标系
   * @returns
   */
  static changeWidgetPosition(widget: WidgetObject, position: [number, number, number], matrix: Matrix4) {
    const newWidget = { ...widget };

    // 把 position 转到文件坐标系
    const widgetPosition = Point3d.pointApplyMatrix4(position, matrix);

    switch (AnnoFactory.getWidgetType(widget)) {
      case 'cuboid':
      case 'point3d':
        newWidget.data = [...widgetPosition, ...newWidget.data.slice(3)];
        break;
      case 'line3d':
      case 'spline3d':
      case 'poly3d':
        newWidget.data = Line3d.getMovedGeometryToPosition(newWidget.data, widgetPosition as [number, number, number]);
        break;
      default:
        break;
    }
    return newWidget;
  }

  /**
   * 移动 target 到指定的 position
   * @param target
   * @param position
   */
  static changePosition(target: Object3D, position: [number, number, number]) {
    switch (target.userData.type) {
      case 'line3d':
        // 折线的 position 第一个点作为基准
        const vertexData = Line3d.getVertexFromObject3D(target as FatLine);

        // 临时移动 选择态 才显示的 点序号
        target.children.forEach((obj: Object3D) => {
          if ('isCSS2DObject' in obj && obj.isCSS2DObject) {
            // 中心坐标相对基准点偏移
            const deltaX = obj.position.x - vertexData[0];
            const deltaY = obj.position.y - vertexData[1];
            obj.position.setX(position[0] + deltaX);
            obj.position.setY(position[1] + deltaY);
          } else if (obj.userData?.seriesPoint?.isAdsorb) {
            // 吸附的添加点跟随引导线移动
            const [x, y, z] = obj.position;
            const deltaX = x - vertexData[0];
            const deltaY = y - vertexData[1];
            const deltaZ = z - vertexData[2];
            const newPosition = [deltaX + position[0], deltaY + position[1], deltaZ + position[2]];
            Point3d.update(obj as Mesh<BoxGeometry, MeshBasicMaterial>, newPosition);
          }
        });

        // 移动线
        const newData = Line3d.getMovedGeometryToPosition(vertexData, position);
        Line3d.update(target as FatLine, newData);

        break;
      default:
        break;
    }
  }

  static changeColor(target: Object3D, color: string, refreshData = true) {
    switch (target.userData.type) {
      case 'box2d':
        Box2d.changeColor(target as Mesh<ShapeGeometry, MeshBasicMaterial>, color, refreshData);
        break;
      case 'cuboid':
        Cuboid.changeColor(target as Mesh<BufferGeometry, MeshBasicMaterial>, color, refreshData);
        break;
      case 'line3d':
      case 'spline3d':
      case 'poly3d':
        Line3d.changeColor(target as FatLine, color, refreshData);
        break;
      case 'line2d':
        Line2d.changeColor(target as FatLine, color, refreshData);
        break;
      case 'point3d':
        Point3d.changeColor(target as Mesh<BoxGeometry, MeshBasicMaterial>, color, refreshData);
        break;
      case 'pyramid':
        Pyramid3d.changeColor(target as Mesh<PolyhedronGeometry, MeshBasicMaterial>, color, refreshData);
        break;
      default:
        break;
    }
  }

  static changePointsSize(target: Object3D, size: number) {
    switch (target.userData.type) {
      case 'line3d':
      case 'spline3d':
      case 'poly3d':
        Line3d.changePointsSize(target as FatLine, size);
        break;
      default:
        break;
    }
  }

  static getDirectionFromWidget(widget: WidgetObject) {
    switch (widget.name) {
      case 'cuboid':
        return Cuboid.getDirectionFromWidget(widget);
      default:
        break;
    }
  }

  static generateObjectZ(object: Object3D) {
    return object.id < CAMERA_FAR_2D * CAMERA_FAR_2D
      ? object.id / CAMERA_FAR_2D
      : parseFloat((Math.random() * CAMERA_FAR_2D).toFixed(2));
  }

  static setStatus(target: Object3D, status: IObject3DStatus) {
    switch (status) {
      case 'hover':
        EnhancedObject3D.setBorderColor(target, OBJECT_EDIT_YELLOW);
        break;
      case 'select':
        if (BORDER_OBJECTS.includes(target.userData.type)) {
          EnhancedObject3D.setBorderThickness(target, BORDER_THICKNESS_BOLD);
        }
        AnnoFactory.changePointsSize(target, Line3d.POINT_SIZE + POINT_SIZE_INCREMENT);
        EnhancedObject3D.setBorderColor(target, OBJECT_EDIT_YELLOW);
        break;

      case 'adjust':
      case 'add':
        if (BORDER_OBJECTS.includes(target.userData.type)) {
          EnhancedObject3D.setBorderThickness(target, BORDER_THICKNESS_NORMAL);
        }
        AnnoFactory.changePointsSize(target, Line3d.POINT_SIZE);
        AnnoFactory.changeColor(target, OBJECT_EDIT_YELLOW, false);
        EnhancedObject3D.toggleLabelVisibility(target, false);
        break;

      default:
        if (BORDER_OBJECTS.includes(target.userData.type)) {
          EnhancedObject3D.setBorderThickness(target, BORDER_THICKNESS_NORMAL);
        }
        AnnoFactory.changePointsSize(target, Line3d.POINT_SIZE);
        AnnoFactory.changeColor(target, target.userData.color || '#FFFFFF', false);
        EnhancedObject3D.toggleLabelVisibility(target, true);
        break;
    }
    target.userData.status = status;

    return;
  }

  /**
   * 从 anno 的 widget 中获取 3D 对象的顶点坐标数组，当前仅支持 立体框，3D 折线，3D 点
   * @param widget
   * @returns
   */
  static getVertexFromWidget(widget: WidgetObject) {
    switch (widget.name) {
      case 'cuboid':
        return Cuboid.getVertexFromWidget(widget.data);
      case 'line3d':
        return widget.data;
      case 'point3d':
        return widget.data;
      default:
        return [];
    }
  }

  /**
   * 从 3D 标注物上获取顶点坐标数组
   */
  static getVertexFromObject3D(widgetType: AnnoHandlerType | AdditionHandlerType, object3D: Object3D) {
    switch (widgetType) {
      case 'line3d':
      case 'caliper':
      case 'caliper-h':
      case 'caliper-v':
        return Line3d.getVertexFromObject3D(object3D as FatLine);
      case 'spline3d':
        return Spline3d.getVertexFromObject3D(object3D as FatLine);
      case 'poly3d':
        return Poly3d.getVertexFromObject3D(object3D as FatLine);
    }
    return [];
  }

  static getFusionMapped(
    widgetType: WidgetType,
    vertexs: number[],
    matrix: Matrix4,
    cameraModel: BaseCamera,
    imageSize?: { width: number; height: number }
  ): WidgetObject | undefined {
    switch (widgetType) {
      case 'cuboid':
        return Cuboid.getFusionMapped(vertexs, matrix, cameraModel, imageSize);
      case 'line3d':
        return Line3d.getFusionMapped(vertexs, matrix, cameraModel, imageSize);
      case 'point3d':
        return Point3d.getFusionMapped(vertexs, matrix, cameraModel);
      default:
        return undefined;
    }
  }

  static isMappedType(widgetName: WidgetType) {
    return widgetName === 'box2d' || widgetName === 'line2d' || widgetName === 'point2d';
  }

  static getWidgetType(widget: WidgetObject) {
    if (widget.name === 'line3d') {
      if (widget.line_type === 'crspline') {
        return 'spline3d';
      } else {
        return 'line3d';
      }
    } else {
      return widget.name;
    }
  }

  /**
   * 获取 3D 对象显示到相机视野中央的 center 向量
   * @param object
   * @returns
   */
  static getAnnoCenter(object: Object3D) {
    switch (object.userData.type) {
      case 'cuboid':
      case 'point3d':
      case 'pyramid':
        return object.position;
      case 'line3d':
      case 'spline3d':
      case 'poly3d':
        return new Vector3().fromArray(
          (object as Mesh<BufferGeometry, MeshBasicMaterial>).geometry.attributes.instanceStart.array
        );
      default:
        break;
    }
  }

  /**
   * 从关键帧中得到当前 index 的标注物
   * @param beginItem 之前的关键帧
   * @param endItem 之后的关键帧
   * @param index 在 count 只中是第几个，从 1 开始，最大是 count
   * @param count 关键帧中间的插值个数
   * @returns
   */
  static getInterpolatedAnnoWidget(
    beginItemWidget: AnnoInstanceItem['widget'],
    endItemWidget: AnnoInstanceItem['widget'] | null = null,
    index: number,
    count: number
  ) {
    switch (beginItemWidget.name) {
      case 'cuboid':
        return Cuboid.getInterpolatedCuboidWidget(beginItemWidget, endItemWidget, index, count);
      default:
        break;
    }
  }

  /**
   * 关键帧需要保持相同的 Widget 局部数据
   * @param source
   * @param target
   * @returns
   */
  static keepManualAnnoWidget(source: AnnoInstanceItem, target: AnnoInstanceItem, oldSourceWidget?: WidgetObject) {
    switch (source.widget.name) {
      case 'cuboid':
        return Cuboid.changeToSameScaleAndDirection(source, target, oldSourceWidget);
      default:
        break;
    }
  }

  /**
   * 释放 threejs 对象渲染内存
   * @param target
   */
  static dispose(target: Object3D) {
    target.traverse((child: any) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
    });
  }

  /**
   * 计算得到 widget 经过 matrix 变换后的新的 widget
   */
  static getTransformedWidget(widget: WidgetObject, matrix: Matrix4) {
    switch (widget.name) {
      case 'cuboid':
        return Cuboid.getTransformedWidget(widget, matrix);
      default:
        break;
    }
    return null;
  }
}
